import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Action,
  Alert,
  Card,
  Modal,
  NumberInput,
  Page,
  PageStatus,
  SimpleSelect,
  TextInput,
  useToast,
} from 'corso-admin-ui';
import { CommandAdminApi, commandPlatformApi } from 'corso-types';
import { FormEventHandler, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import api from '~/api';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from '~/components/dataTable';
import { useProtectSets } from '~/hooks/useProtectSets';

type ProtectSet = CommandAdminApi.ResponseBody<
  '/ship-protect-set',
  'get'
>[number];

const { idColumn, nameColumn, storeCountColumn, createdOnColumn } =
  sharedTableDefinitions;

const protectSetColumns: ColumnDef<ProtectSet>[] = [
  idColumn<ProtectSet>({ meta: { align: 'left' } }),
  nameColumn<ProtectSet>(),
  { accessorKey: 'displayName', header: 'Display Name' },
  { accessorKey: 'description', header: 'Description' },
  { accessorKey: 'code', header: 'Code' },
  storeCountColumn<ProtectSet>(),
  createdOnColumn<ProtectSet>(),
];

const createSchema = commandPlatformApi['/ship-protect-set'].post.request.body;

function useCreatePricingSet() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (body: z.infer<typeof createSchema>) =>
      api.shipProtectSets.create(body),
    onSuccess: () => {
      toast.show('Created pricing set');
      return queryClient.invalidateQueries({
        queryKey: ['shipProtectSets'],
      });
    },
  });
}

const intervals = [25, 50, 100, 200, 250] as const;

// An interval must be smaller than the band it divides and divide it evenly.
const validIntervalOptions = (bandSize: number) =>
  intervals
    .filter((interval) => interval < bandSize && bandSize % interval === 0)
    .map((interval) => ({ value: `${interval}`, label: `$${interval}` }));

const emptyTier = {
  cartValueStart: 0,
  cartValueEnd: 100,
  chargePercentage: 3,
  unitInterval: 25,
} as const;

function CreatePricingSetForm({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    reset,
    register,
  } = useForm({
    resolver: zodResolver(createSchema),
    defaultValues: { name: '', displayName: '', tiers: [emptyTier] },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'tiers' });

  const watchedTiers = useWatch({ control, name: 'tiers' });

  // Tiers whose cart range can't be evenly divided by any allowed interval.
  const tierIssues = watchedTiers.flatMap((tier, index) => {
    const { cartValueStart, cartValueEnd } = tier;
    if (!Number.isFinite(cartValueStart) || !Number.isFinite(cartValueEnd))
      return [];

    const bandSize = cartValueEnd - cartValueStart;
    if (validIntervalOptions(bandSize).length) return [];

    return [
      bandSize <= 0
        ? `Tier ${index + 1}: Cart Value End must be greater than Cart Value Start.`
        : `Tier ${index + 1}: a $${bandSize} range has no valid interval — set End − Start to a multiple of $25 (at least $50 wide).`,
    ];
  });

  const appendTier = () => {
    const tiers = getValues('tiers');
    const cartValueStart = tiers.at(-1)?.cartValueEnd ?? 0;
    append({
      ...emptyTier,
      cartValueStart,
      cartValueEnd: cartValueStart + 100,
    });
  };

  const { mutate: createPricingSet, error, isPending } = useCreatePricingSet();

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((formValues) => {
      createPricingSet(formValues, {
        onSuccess: () => {
          reset();
          onClose();
        },
      });
    })(event).catch(console.error);
  };

  return (
    <Modal show={show} onClose={onClose} title="Create Pricing Set" size="xl">
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <TextInput
          id="name"
          label="Name"
          required
          {...register('name')}
          error={errors.name?.message}
        />
        <TextInput
          id="displayName"
          label="Display Name"
          {...register('displayName')}
          error={errors.displayName?.message}
        />

        <div className="flex flex-col gap-4">
          <Card.Line>
            <Card.Heading>Tiers</Card.Heading>
            <Action
              variant="ghost"
              type="button"
              className="self-end"
              icon="add"
              accessibilityLabel="Add Tier"
              onClick={appendTier}
            />
          </Card.Line>
          {fields.map((field, index) => {
            const tier = watchedTiers[index];
            const bandSize =
              (tier?.cartValueEnd ?? 0) - (tier?.cartValueStart ?? 0);
            const intervalOptions = validIntervalOptions(bandSize);

            return (
              <div
                key={field.id}
                className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] items-start gap-3 rounded-md border border-neutral-300 p-3"
              >
                <NumberInput
                  id={`tiers.${index}.cartValueStart`}
                  label="Cart Value Start"
                  required
                  step="any"
                  addon={{ insideStart: '$' }}
                  {...register(`tiers.${index}.cartValueStart`, {
                    valueAsNumber: true,
                  })}
                  error={errors.tiers?.[index]?.cartValueStart?.message}
                />
                <NumberInput
                  id={`tiers.${index}.cartValueEnd`}
                  label="Cart Value End"
                  required
                  step="any"
                  addon={{ insideStart: '$' }}
                  {...register(`tiers.${index}.cartValueEnd`, {
                    valueAsNumber: true,
                  })}
                  error={errors.tiers?.[index]?.cartValueEnd?.message}
                />
                <NumberInput
                  id={`tiers.${index}.chargePercentage`}
                  label="Charge"
                  required
                  step="any"
                  addon={{ insideEnd: '%' }}
                  {...register(`tiers.${index}.chargePercentage`, {
                    valueAsNumber: true,
                  })}
                  error={errors.tiers?.[index]?.chargePercentage?.message}
                />
                {/* -mt-1 offsets SimpleSelect's gap-2 so its control lines up with the gap-1 inputs */}
                <Controller
                  control={control}
                  name={`tiers.${index}.unitInterval`}
                  render={({ field: select, fieldState }) => (
                    <div className="-mt-1">
                      <SimpleSelect
                        label="Interval"
                        required
                        disabled={!intervalOptions.length}
                        placeholder={
                          intervalOptions.length
                            ? 'Select an option'
                            : 'No valid interval'
                        }
                        options={intervalOptions}
                        value={`${select.value}`}
                        onChange={(value) =>
                          select.onChange(Number.parseInt(value, 10))
                        }
                        error={fieldState.error?.message}
                      />
                    </div>
                  )}
                />
                <Action
                  variant="ghost"
                  type="button"
                  accessibilityLabel="Remove Tier"
                  className="self-end"
                  onClick={() => remove(index)}
                  icon="remove"
                  disabled={fields.length === 1}
                />
              </div>
            );
          })}
        </div>

        {tierIssues.length ? (
          <Alert heading="Invalid Tier Detected" variant="warning">
            <ul className="list-disc pl-4">
              {tierIssues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </Alert>
        ) : null}

        {error ? (
          <Alert heading="Critical Configuration Error" variant="critical">
            {error.message}
          </Alert>
        ) : null}

        <Action
          variant="primary"
          type="submit"
          disabled={isPending || tierIssues.length > 0}
          loading={isPending}
        >
          Create
        </Action>
      </form>
    </Modal>
  );
}

export default function AllPricingSetsPage() {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const query = useProtectSets();

  return (
    <Page
      title="Pricing Sets"
      size="fullWidth"
      primaryAction={{
        onAction: () => setShowCreate(true),
        content: 'Create Pricing Set',
      }}
    >
      {match(query)
        .with({ status: 'pending' }, () => (
          <PageStatus.Loading title="Loading pricing sets" />
        ))
        .with({ status: 'error' }, () => (
          <PageStatus.Error
            title="Failed to load pricing sets"
            error={query.error}
          />
        ))
        .with({ status: 'success', data: P.select() }, (data) =>
          data.length > 0 ? (
            <Card>
              <DataTable
                data={data}
                columns={protectSetColumns}
                onRowClick={(row) => navigate(`${row.id}`)}
                filterable
              />
            </Card>
          ) : (
            <DataTableStatus
              icon="tags"
              title="No pricing sets found"
              details="Create a pricing set to get started."
            />
          ),
        )
        .exhaustive()}

      <CreatePricingSetForm
        show={showCreate}
        onClose={() => setShowCreate(false)}
      />
    </Page>
  );
}
